import {Component, DestroyRef, OnInit, computed, inject, input, output,} from "@angular/core";
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators,} from "@angular/forms";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatIcon } from "@angular/material/icon";
import { Store } from "@ngrx/store";
import {EMPTY,Observable,catchError, debounceTime,distinctUntilChanged,finalize,map,of,switchMap,tap,} from "rxjs";
import {ActivarLoadingAction,DesactivarLoadingAction,} from "src/app/@store/actions";
import { AppState } from "src/app/@store/app.reducers";
import { BLOCK_UI_REQUEST } from "src/app/helper/seguros-atlas/seguros-atlas.helper";
import {IResponseValidaContrasena,Validacion,} from "src/app/models/administracion-usuarios/administracion-usuarios.model";
import {INormaModelInput,IResponseModel,} from "src/app/models/seguros-atlas/atlas.model";
import { AdministrarUsuariosService } from "src/app/services/administracion-usuarios/administrar-usuarios.service";
import { AuthService } from "src/app/services/auth/auth.service";
import ValidationRegistro from "../../validators/registro.validators";
import { RegistroInformativoDialogComponent } from "../dialogs/registro-informativo-dialog/registro-informativo-dialog.component";
import { RegistroValidadorDosFaComponent } from "../dialogs/registro-validador-dos-fa/registro-validador-dos-fa.component";

interface CatalogoPregunta {
  Text: string;
  Value: string | number;
}

interface LadaTelefonicaInternacionalResponse {
  valor_dato: string | number;
  descripl_dato: string;
}

interface CodigoPaisTelefono {
  codigo: string;
  pais: string;
  bandera: string;
}

interface ResultadoValidacionUsuario {
  valido: boolean;
  mensaje: string;
}

interface RespuestaApi<T> {
  Errores?: unknown[];
  Respuesta?: T;
}

interface ExistenciaUsuarioSisa {
  existe: boolean;
}

interface RegistroEmpleadoEjecutivoConfig {
  perfilNombre: TipoRegistroEmpleadoEjecutivo;
  mostrarCuenta: boolean;
  etiquetaCuenta: string;
  placeholderCuenta: string;
}

interface RegistroEmpleadoRequest {
  NumeroEmpleado: string;
  Usuario: string;
  Password: string;
  ConfirmaPassword: string;
  Alias: string;
  Telefono: number;
  CodTelefono: string;
  Email: string;
  CodEmail: string;
  IdPregunta: number;
  Respuesta: string;
  Nombre: string;
  Paterno: string;
  Materno: string;
  RFC: string;
  TipoPersona: number;
  Nacionalidad: string;
  RazonSocial: string;
  ValidarUsuario: string;
}

interface RegistroEjecutivoRequest extends RegistroEmpleadoRequest {
  Cuenta: string;
}

type ContactoControlName = "Email" | "Telefono";
export type TipoRegistroEmpleadoEjecutivo = "Empleado" | "Ejecutivo";

const RFC_PATTERN =
  /^([A-ZÑ&]{3,4}\d{2}(0[1-9]|1[0-2])(0[1-9]|[12]\d|3[01]))[A-Z0-9]{3}$/i;

const REGISTRO_CONFIG: Record<
  TipoRegistroEmpleadoEjecutivo,
  RegistroEmpleadoEjecutivoConfig
> = {
  Empleado: {
    perfilNombre: "Empleado",
    mostrarCuenta: false,
    etiquetaCuenta: "",
    placeholderCuenta: "",
  },
  Ejecutivo: {
    perfilNombre: "Ejecutivo",
    mostrarCuenta: true,
    etiquetaCuenta: "Clave de Ejecutivo",
    placeholderCuenta: "Ingresa tu clave de ejecutivo",
  },
};

@Component({
  selector: "app-registro-empleado-ejecutivo-form",
  standalone: true,
  templateUrl: "./registro-empleado-ejecutivo-form.component.html",
  styleUrls: ["./registro-empleado-ejecutivo-form.component.scss"],
  imports: [ReactiveFormsModule, MatIcon],
})
export class RegistroEmpleadoEjecutivoFormComponent implements OnInit {
  readonly tipoRegistro = input.required<TipoRegistroEmpleadoEjecutivo>();
  readonly regresarPerfil = output<void>();
  readonly registroCompletado = output<string>();
  readonly formularioModificado = output<boolean>();

  readonly configuracionRegistro = computed(
    () => REGISTRO_CONFIG[this.tipoRegistro()],
  );

  readonly formularioRegistro = new FormGroup(
    {
      NumeroEmpleado: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Usuario: new FormControl("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9_]*$/),
          Validators.minLength(4),
          Validators.maxLength(8),
        ],
      }),
      Cuenta: new FormControl("", {
        nonNullable: true,
      }),
      Password: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      ConfirmaPassword: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Alias: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Email: new FormControl("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.email,
          Validators.pattern(/^[^@\s]+@[^@\s]+\.[a-zA-Z]{2,}$/),
        ],
      }),
      Telefono: new FormControl("", {
        nonNullable: true,
        validators: [Validators.required, Validators.pattern(/^\d{11,15}$/)],
      }),
      CodEmail: new FormControl("", { nonNullable: true }),
      CodTelefono: new FormControl("", { nonNullable: true }),
      IdPregunta: new FormControl<number | string | null>(
        null,
        Validators.required,
      ),
      Respuesta: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Nombre: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Paterno: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      Materno: new FormControl("", {
        nonNullable: true,
        validators: Validators.required,
      }),
      RFC: new FormControl("", {
        nonNullable: true,
        validators: [
          Validators.required,
          Validators.pattern(RFC_PATTERN),
          Validators.maxLength(13),
        ],
      }),
      TipoPersona: new FormControl(1, { nonNullable: true }),
      Nacionalidad: new FormControl("1", { nonNullable: true }),
      RazonSocial: new FormControl("", { nonNullable: true }),
      ValidarUsuario: new FormControl("false", { nonNullable: true }),
    },
    {
      validators: ValidationRegistro.match("Password", "ConfirmaPassword"),
    },
  );

  hidePassword = true;
  hideConfirmPassword = true;
  loadingRegistro = false;

  usuarioValido = { is: false, pending: false };
  validacionUsuario = "";
  actualizarUsuario = "false";

  validandoPassword = false;
  passwordValido = false;
  generaErrorPasswordForm = false;
  passwordValidaciones: string[] = [];

  validandoCorreo = false;
  validandoTelefono = false;
  dosFaCorreoHabilitado = true;
  dosFaTelefonoHabilitado = true;
  consultandoNormaCorreo = true;
  consultandoNormaTelefono = true;
  procesandoCorreo = false;
  procesandoTelefono = false;
  twoFaCorreoAbierto = false;
  twoFaTelefonoAbierto = false;
  emailValidado = "";
  telefonoValidado = "";

  catPreguntas: CatalogoPregunta[] = [];
  codigosPaisTelefono: CodigoPaisTelefono[] = [
    { codigo: "52", pais: "México", bandera: "MX" },
  ];
  codigoPaisTelefonoSeleccionado = this.codigosPaisTelefono[0];
  telefonoLocal = "";

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store<AppState>);
  private confirmandoCambioCorreo = false;
  private confirmandoCambioTelefono = false;
  private normaCorreoConsultada = false;
  private normaTelefonoConsultada = false;
  private consultasNormasPendientes = 0;

  constructor(
    private readonly authService: AuthService,
    private readonly adminService: AdministrarUsuariosService,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.cargarConfiguracionDosFa();
    this.configurarControlCuenta();
    this.configurarValidacionUsuario();
    this.configurarValidacionPassword();
    this.configurarSincronizacionConfirmacionPassword();
    this.configurarCambiosContacto();
    this.cargarCatalogoPreguntas();
    this.cargarLadasTelefonicasInternacionales();
    this.formularioRegistro.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.formularioModificado.emit(
          this.formularioRegistro.dirty
        );
      });
  }

  regresar(): void {
    this.regresarPerfil.emit();
  }

  private cargarConfiguracionDosFa(): void {
    this.consultasNormasPendientes = 2;
    this.store.dispatch(ActivarLoadingAction());

    this.consultarConfiguracionDosFaCorreo();
    this.consultarConfiguracionDosFaTelefono();
  }

  private finalizarCargaInicialNorma(): void {
    this.consultasNormasPendientes = Math.max(
      0,
      this.consultasNormasPendientes - 1,
    );

    if (this.consultasNormasPendientes === 0) {
      this.store.dispatch(DesactivarLoadingAction());
    }
  }

  private consultarConfiguracionDosFaCorreo(): void {
    this.consultandoNormaCorreo = true;

    this.authService
      .obtenerNormaAtlas(this.crearNorma(763))
      .pipe(
        finalize(() => {
          this.consultandoNormaCorreo = false;
          this.finalizarCargaInicialNorma();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (respuesta: IResponseModel) => {
          this.aplicarConfiguracionDosFaCorreo(respuesta);
        },
        error: () => {
          this.normaCorreoConsultada = false;
          this.dosFaCorreoHabilitado = true;
        },
      });
  }

  private consultarConfiguracionDosFaTelefono(): void {
    this.consultandoNormaTelefono = true;

    this.authService
      .obtenerNormaAtlas(this.crearNorma(765))
      .pipe(
        finalize(() => {
          this.consultandoNormaTelefono = false;
          this.finalizarCargaInicialNorma();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (respuesta: IResponseModel) => {
          this.aplicarConfiguracionDosFaTelefono(respuesta);
        },
        error: () => {
          this.normaTelefonoConsultada = false;
          this.dosFaTelefonoHabilitado = true;
        },
      });
  }

  private aplicarConfiguracionDosFaCorreo(
    respuesta: IResponseModel,
  ): void {
    this.normaCorreoConsultada = true;
    this.dosFaCorreoHabilitado = respuesta?.Respuesta?.Status == 1;

    if (!this.dosFaCorreoHabilitado) {
      this.formularioRegistro.controls.CodEmail.setValue("9000");
      this.validandoCorreo = false;
      this.emailValidado = "";
      return;
    }

    if (this.formularioRegistro.controls.CodEmail.value === "9000") {
      this.formularioRegistro.controls.CodEmail.setValue("");
    }
  }

  private aplicarConfiguracionDosFaTelefono(
    respuesta: IResponseModel,
  ): void {
    this.normaTelefonoConsultada = true;
    this.dosFaTelefonoHabilitado = respuesta?.Respuesta?.Status == 1;

    if (!this.dosFaTelefonoHabilitado) {
      this.formularioRegistro.controls.CodTelefono.setValue("9000");
      this.validandoTelefono = false;
      this.telefonoValidado = "";
      return;
    }

    if (this.formularioRegistro.controls.CodTelefono.value === "9000") {
      this.formularioRegistro.controls.CodTelefono.setValue("");
    }
  }

  private configurarControlCuenta(): void {
    const control = this.formularioRegistro.controls.Cuenta;

    if (this.configuracionRegistro().mostrarCuenta) {
      control.enable({ emitEvent: false });
      control.setValidators(Validators.required);
    } else {
      control.clearValidators();
      control.setValue("", { emitEvent: false });
      control.disable({ emitEvent: false });
    }

    control.updateValueAndValidity({ emitEvent: false });
  }

  continuar(): void {
    this.formularioRegistro.markAllAsTouched();
    this.sincronizarTelefonoCompleto(true);
    this.sincronizarErrorConfirmaPassword();

    if (!this.usuarioValido.is) {
      const control = this.formularioRegistro.controls.Usuario;
      control.setErrors({
        ...(control.errors || {}),
        customError: true,
      });
      this.validacionUsuario =
        this.validacionUsuario || "Debe ingresar un usuario válido.";
    }

    const errores = this.obtenerErroresRegistro();

    if (errores.length) {
      this.mostrarErroresRegistro(errores);
      return;
    }

    this.registrar();
  }

  private registrar(): void {
    if (this.loadingRegistro) {
      return;
    }

    const payload = this.construirPayload();
    this.loadingRegistro = true;
    this.store.dispatch(ActivarLoadingAction());

    const registro$: Observable<unknown> =
      this.tipoRegistro() === "Empleado"
        ? this.authService.registrarEmpleado(payload as any)
        : this.authService.registrarEjecutivo(payload as any);

    registro$
      .pipe(
        finalize(() => {
          this.loadingRegistro = false;
          this.store.dispatch(DesactivarLoadingAction());
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: () => {
          this.registroCompletado.emit(payload.Usuario);
        },
        error: (error) => {
          this.abrirDialogoErroresBackend(error);
        },
      });
  }

  private construirPayload():
    | RegistroEmpleadoRequest
    | RegistroEjecutivoRequest {
    const raw = this.formularioRegistro.getRawValue();

    const payloadBase: RegistroEmpleadoRequest = {
      NumeroEmpleado: `${raw.NumeroEmpleado || ""}`.trim(),
      Usuario: `${raw.Usuario || ""}`.trim(),
      Password: `${raw.Password || ""}`,
      ConfirmaPassword: `${raw.ConfirmaPassword || ""}`,
      Alias: `${raw.Alias || ""}`.trim(),
      Telefono: Number(raw.Telefono),
      CodTelefono: `${raw.CodTelefono || ""}`,
      Email: `${raw.Email || ""}`.trim(),
      CodEmail: `${raw.CodEmail || ""}`,
      IdPregunta: Number(raw.IdPregunta),
      Respuesta: `${raw.Respuesta || ""}`.trim(),
      Nombre: `${raw.Nombre || ""}`.trim(),
      Paterno: `${raw.Paterno || ""}`.trim(),
      Materno: `${raw.Materno || ""}`.trim(),
      RFC: `${raw.RFC || ""}`.trim().toUpperCase(),
      TipoPersona: Number(raw.TipoPersona),
      Nacionalidad: `${raw.Nacionalidad || "1"}`,
      RazonSocial: `${raw.RazonSocial || ""}`.trim(),
      ValidarUsuario: this.actualizarUsuario,
    };

    if (this.tipoRegistro() === "Empleado") {
      return payloadBase;
    }

    return {
      ...payloadBase,
      Cuenta: `${raw.Cuenta || ""}`.trim(),
    };
  }

  private configurarValidacionUsuario(): void {
    const control = this.formularioRegistro.controls.Usuario;

    control.valueChanges
      .pipe(
        tap(() => {
          this.usuarioValido.pending = true;
          this.usuarioValido.is = false;
          this.validacionUsuario = "";
        }),
        debounceTime(1000),
        distinctUntilChanged(),
        switchMap((usuario) => this.ejecutarValidacionUsuario(usuario)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((resultado) => {
        this.usuarioValido.pending = false;
        this.usuarioValido.is = resultado.valido;
        this.validacionUsuario = resultado.mensaje;

        if (resultado.valido) {
          this.limpiarErroresControl(control, [
            "exists",
            "customError",
            "minlengthCustom",
            "maxlengthCustom",
          ]);

          if (this.validandoCorreo || this.validandoTelefono) {
            this.actualizarUsuario = "true";
          }
        } else if (control.value) {
          control.setErrors({
            ...(control.errors || {}),
            customError: true,
          });
        }

        const passwordControl = this.formularioRegistro.controls.Password;
        if (passwordControl.value) {
          passwordControl.setValue(passwordControl.value, {
            emitEvent: true,
          });
        }
      });
  }

  private ejecutarValidacionUsuario(
    usuario: string,
  ): Observable<ResultadoValidacionUsuario> {
    const valor = `${usuario || ""}`.trim();
    const control = this.formularioRegistro.controls.Usuario;

    if (!valor) {
      return of({ valido: false, mensaje: "" });
    }

    if (!/^[a-zA-Z0-9_]*$/.test(valor)) {
      return of({
        valido: false,
        mensaje: "Solo se permiten letras, números y guion bajo.",
      });
    }

    if (valor.length < 4) {
      control.setErrors({
        ...(control.errors || {}),
        minlengthCustom: true,
      });
      return of({
        valido: false,
        mensaje: "El nombre de usuario debe tener al menos 4 caracteres.",
      });
    }

    if (valor.length > 8) {
      control.setErrors({
        ...(control.errors || {}),
        maxlengthCustom: true,
      });
      return of({
        valido: false,
        mensaje: "El usuario solo puede tener hasta 8 caracteres.",
      });
    }

    return this.authService
      .VerificarExistenciaUser({ Usuario: valor })
      .pipe(
        switchMap((respuesta: RespuestaApi<ExistenciaUsuarioSisa>) => {
          if (respuesta?.Respuesta?.existe !== true) {
            return of({
              valido: false,
              mensaje:
                "Clave de usuario incorrecta. Verifica los datos e intenta de nuevo.",
            });
          }

          return this.authService.validaUsuario(valor).pipe(
            map(() => ({ valido: true, mensaje: "" })),
            catchError(() =>
              of({
                valido: false,
                mensaje: "El usuario no está disponible.",
              }),
            ),
          );
        }),
        catchError((error) =>
          of({
            valido: false,
            mensaje:
              error?.error?.Exception ||
              error?.error?.Message ||
              "No fue posible validar el usuario. Inténtalo nuevamente.",
          }),
        ),
      );
  }

  private configurarValidacionPassword(): void {
    const control = this.formularioRegistro.controls.Password;

    control.valueChanges
      .pipe(
        tap(() => {
          this.validandoPassword = true;
          this.passwordValido = false;
        }),
        debounceTime(1000),
        switchMap((password) => {
          if (!`${password || ""}`.trim()) {
            this.reiniciarValidacionesContrasena();
            return EMPTY;
          }

          return this.adminService
            .postValidaContrasena({
              password,
              usuario: this.formularioRegistro.controls.Usuario.value,
            })
            .pipe(
              catchError((error) =>
                of({
                  error,
                  datos: null,
                }),
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((respuesta: any) => {
        if (respuesta?.error) {
          this.procesarErrorValidacionPassword(respuesta.error);
          return;
        }

        this.procesarRespuestaValidacionPassword(
          respuesta as IResponseValidaContrasena,
        );
      });
  }

  private procesarRespuestaValidacionPassword(
    respuesta: IResponseValidaContrasena,
  ): void {
    const control = this.formularioRegistro.controls.Password;
    this.validandoPassword = false;

    if (respuesta?.datos?.length) {
      this.generaErrorPasswordForm = true;
      this.passwordValido = false;
      this.passwordValidaciones = this.normalizarMensajesPassword(
        respuesta.datos,
      );
      control.setErrors({
        ...(control.errors || {}),
        formatoIncorrecto: true,
      });
      this.sincronizarErrorConfirmaPassword();
      return;
    }

    this.generaErrorPasswordForm = false;
    this.passwordValido = true;
    this.passwordValidaciones = [];
    this.limpiarErroresControl(control, ["formatoIncorrecto"]);
    this.sincronizarErrorConfirmaPassword();
  }

  private procesarErrorValidacionPassword(error: any): void {
    const control = this.formularioRegistro.controls.Password;
    this.validandoPassword = false;
    this.passwordValido = false;
    this.generaErrorPasswordForm = true;
    this.passwordValidaciones = this.normalizarMensajesPassword(error?.error);

    if (!this.passwordValidaciones.length) {
      this.passwordValidaciones = [
        "No fue posible comprobar el formato de la contraseña.",
      ];
    }

    control.setErrors({
      ...(control.errors || {}),
      formatoIncorrecto: true,
    });
    this.sincronizarErrorConfirmaPassword();
  }

  private configurarSincronizacionConfirmacionPassword(): void {
    this.formularioRegistro.controls.Password.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sincronizarErrorConfirmaPassword());

    this.formularioRegistro.controls.ConfirmaPassword.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.sincronizarErrorConfirmaPassword());
  }

  private sincronizarErrorConfirmaPassword(): void {
    const password = this.formularioRegistro.controls.Password.value;
    const confirmaControl = this.formularioRegistro.controls.ConfirmaPassword;
    const confirma = confirmaControl.value;

    if (!confirma) {
      this.limpiarErroresControl(confirmaControl, ["match"]);
      return;
    }

    if (password !== confirma) {
      confirmaControl.setErrors({
        ...(confirmaControl.errors || {}),
        match: true,
      });
      return;
    }

    this.limpiarErroresControl(confirmaControl, ["match"]);
  }

  abrirErroresPassword(): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: "440px",
      maxWidth: "calc(100vw - 32px)",
      autoFocus: false,
      data: {
        type: "warning",
        icon: "error_outline",
        title: "Revisa tu contraseña",
        message: "La contraseña debe cumplir con las siguientes condiciones:",
        messages: this.passwordValidaciones.length
          ? this.passwordValidaciones
          : ["No fue posible obtener el detalle de las reglas."],
        actionText: "Entendido",
      },
    });
  }

  mostrarIconoPasswordError(): boolean {
    const control = this.formularioRegistro.controls.Password;
    return (
      control.hasError("formatoIncorrecto") &&
      (control.touched || control.dirty)
    );
  }

  mostrarIconoPasswordCorrecto(): boolean {
    const control = this.formularioRegistro.controls.Password;
    return (
      !!control.value &&
      control.valid &&
      this.passwordValido &&
      !this.validandoPassword &&
      !this.generaErrorPasswordForm
    );
  }

  mostrarIconoConfirmaPasswordCorrecto(): boolean {
    const password = this.formularioRegistro.controls.Password.value;
    const confirma = this.formularioRegistro.controls.ConfirmaPassword;
    return (
      !!password &&
      !!confirma.value &&
      password === confirma.value &&
      this.passwordValido &&
      !this.validandoPassword &&
      !confirma.errors
    );
  }

  private reiniciarValidacionesContrasena(): void {
    this.validandoPassword = false;
    this.passwordValido = false;
    this.generaErrorPasswordForm = false;
    this.passwordValidaciones = [];
    this.limpiarErroresControl(this.formularioRegistro.controls.Password, [
      "formatoIncorrecto",
    ]);
  }

  private normalizarMensajesPassword(validaciones: any): string[] {
    if (!validaciones) {
      return [];
    }

    const elementos = Array.isArray(validaciones)
      ? validaciones
      : [validaciones];

    return elementos
      .map((item: Validacion | any) => {
        if (typeof item === "string") {
          return item;
        }

        return (
          item?.Descripcion ||
          item?.descripcion ||
          item?.Exception ||
          item?.Message ||
          item?.message ||
          item?.password?.[0] ||
          ""
        );
      })
      .map((mensaje: string) => `${mensaje}`.trim())
      .filter(Boolean);
  }

  capturarRfc(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value
      .toUpperCase()
      .replace(/[^A-Z0-9Ñ&]/g, "")
      .slice(0, 13);

    input.value = valor;
    this.formularioRegistro.controls.RFC.setValue(valor);
  }

  verificarCorreoAlPerderFoco(): void {
    const control = this.formularioRegistro.controls.Email;
    control.markAsTouched();

    if (!this.dosFaCorreoHabilitado || this.consultandoNormaCorreo) {
      return;
    }

    if (!this.validandoCorreo && !this.procesandoCorreo && control.valid) {
      this.verificarCorreo();
    }
  }

  verificarCorreo(): void {
    const control = this.formularioRegistro.controls.Email;
    control.markAsTouched();

    if (
      !this.dosFaCorreoHabilitado ||
      this.consultandoNormaCorreo ||
      control.invalid ||
      this.validandoCorreo ||
      this.procesandoCorreo ||
      this.twoFaCorreoAbierto
    ) {
      return;
    }

    if (!this.validarUsuarioParaContacto("Email", true)) {
      return;
    }

    if (this.normaCorreoConsultada) {
      this.abrirValidadorDosFaCorreo();
      return;
    }

    this.procesandoCorreo = true;
    this.store.dispatch(ActivarLoadingAction());

    this.authService
      .obtenerNormaAtlas(this.crearNorma(763))
      .pipe(
        finalize(() => {
          this.procesandoCorreo = false;
          this.store.dispatch(DesactivarLoadingAction());
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (respuesta: IResponseModel) => {
          this.aplicarConfiguracionDosFaCorreo(respuesta);

          if (this.dosFaCorreoHabilitado) {
            this.abrirValidadorDosFaCorreo();
          }
        },
        error: () => {
          this.normaCorreoConsultada = false;
          this.dosFaCorreoHabilitado = true;
          this.reiniciarValidacionCorreo();
          this.mostrarErrorSolicitudCodigo();
        },
      });
  }

  verificarTelefonoAlPerderFoco(): void {
    this.sincronizarTelefonoCompleto(true);

    if (!this.dosFaTelefonoHabilitado || this.consultandoNormaTelefono) {
      return;
    }

    if (this.telefonoTieneFormatoCorrecto()) {
      this.verificarTelefono();
    }
  }

  verificarTelefono(): void {
    const control = this.formularioRegistro.controls.Telefono;
    control.markAsTouched();

    if (
      !this.dosFaTelefonoHabilitado ||
      this.consultandoNormaTelefono ||
      !this.telefonoTieneFormatoCorrecto() ||
      this.validandoTelefono ||
      this.procesandoTelefono ||
      this.twoFaTelefonoAbierto
    ) {
      return;
    }

    if (!this.validarUsuarioParaContacto("Telefono", true)) {
      return;
    }

    if (this.normaTelefonoConsultada) {
      this.abrirValidadorDosFaTelefono();
      return;
    }

    this.procesandoTelefono = true;
    this.store.dispatch(ActivarLoadingAction());

    this.authService
      .obtenerNormaAtlas(this.crearNorma(765))
      .pipe(
        finalize(() => {
          this.procesandoTelefono = false;
          this.store.dispatch(DesactivarLoadingAction());
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (respuesta: IResponseModel) => {
          this.aplicarConfiguracionDosFaTelefono(respuesta);

          if (this.dosFaTelefonoHabilitado) {
            this.abrirValidadorDosFaTelefono();
          }
        },
        error: () => {
          this.normaTelefonoConsultada = false;
          this.dosFaTelefonoHabilitado = true;
          this.reiniciarValidacionTelefono();
          this.mostrarErrorSolicitudCodigo();
        },
      });
  }

  private validarUsuarioParaContacto(
    _controlName: ContactoControlName,
    mostrarDialogo: boolean,
  ): boolean {
    if (this.usuarioValido.is && !this.usuarioValido.pending) {
      return true;
    }

    const mensaje = this.usuarioValido.pending
      ? "Espera a que termine la validación del usuario."
      : "Primero ingresa un usuario válido para verificar tus datos de contacto.";

    this.formularioRegistro.controls.Usuario.markAsTouched();

    if (mostrarDialogo) {
      this.abrirDialogoInformativo("warning", "Usuario requerido", mensaje);
    }

    return false;
  }

  private crearNorma(numero: number): INormaModelInput {
    return {
      NumNorma: numero,
      Parametro1: 1,
      Parametro2: 0,
      Parametro3: 0,
    };
  }

  private abrirValidadorDosFaCorreo(): void {
    if (this.twoFaCorreoAbierto) {
      return;
    }

    this.twoFaCorreoAbierto = true;
    const config = this.crearConfiguracionDosFa(
      "Correo Electronico",
      this.formularioRegistro.controls.Email.value,
    );

    this.dialog
      .open(RegistroValidadorDosFaComponent, config)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.twoFaCorreoAbierto = false;
        const codigo = `${result?.TwoFactorCode || ""}`;
        codigo
          ? this.marcarCorreoValidado(codigo)
          : this.reiniciarValidacionCorreo();
      });
  }

  private abrirValidadorDosFaTelefono(): void {
    if (this.twoFaTelefonoAbierto) {
      return;
    }

    this.twoFaTelefonoAbierto = true;
    const config = this.crearConfiguracionDosFa(
      "Telefono",
      this.formularioRegistro.controls.Telefono.value,
    );

    this.dialog
      .open(RegistroValidadorDosFaComponent, config)
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        this.twoFaTelefonoAbierto = false;
        const codigo = `${result?.TwoFactorCode || ""}`;
        codigo
          ? this.marcarTelefonoValidado(codigo)
          : this.reiniciarValidacionTelefono();
      });
  }

  private crearConfiguracionDosFa(
    tipo: string,
    valor: string,
  ): MatDialogConfig {
    return {
      autoFocus: false,
      disableClose: true,
      width: "520px",
      maxWidth: "calc(100vw - 32px)",
      panelClass: "registro-twofa-dialog-panel",
      data: {
        tipo,
        valor,
        usuario: this.formularioRegistro.controls.Usuario.value,
      },
    };
  }

  private marcarCorreoValidado(codigo: string): void {
    this.formularioRegistro.controls.CodEmail.setValue(codigo);
    this.validandoCorreo = true;
    this.emailValidado = this.formularioRegistro.controls.Email.value;
    if (this.validandoTelefono) {
      this.actualizarUsuario = "false";
    }
  }

  private marcarTelefonoValidado(codigo: string): void {
    this.formularioRegistro.controls.CodTelefono.setValue(codigo);
    this.validandoTelefono = true;
    this.telefonoValidado = this.formularioRegistro.controls.Telefono.value;
    this.sincronizarTelefonoVisualDesdeCompleto(this.telefonoValidado);
    if (this.validandoCorreo) {
      this.actualizarUsuario = "false";
    }
  }

  private reiniciarValidacionCorreo(): void {
    this.formularioRegistro.controls.CodEmail.setValue(
      this.dosFaCorreoHabilitado ? "" : "9000",
    );
    this.validandoCorreo = false;
    this.emailValidado = "";
  }

  private reiniciarValidacionTelefono(): void {
    this.formularioRegistro.controls.CodTelefono.setValue(
      this.dosFaTelefonoHabilitado ? "" : "9000",
    );
    this.validandoTelefono = false;
    this.telefonoValidado = "";
  }

  private configurarCambiosContacto(): void {
    this.formularioRegistro.controls.Email.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((email) => {
        if (
          this.validandoCorreo &&
          !this.confirmandoCambioCorreo &&
          email !== this.emailValidado
        ) {
          this.confirmarCambioCorreo();
        }
      });

    this.formularioRegistro.controls.Telefono.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((telefono) => {
        if (
          this.validandoTelefono &&
          !this.confirmandoCambioTelefono &&
          telefono !== this.telefonoValidado
        ) {
          this.confirmarCambioTelefono();
        }
      });
  }

  private confirmarCambioCorreo(): void {
    this.confirmandoCambioCorreo = true;
    this.abrirConfirmacionCambio(
      "Cambiar email",
      "Si cambias el email tendrás que validarlo nuevamente. ¿Deseas continuar?",
    ).subscribe((confirmado) => {
      this.confirmandoCambioCorreo = false;
      if (confirmado) {
        this.reiniciarValidacionCorreo();
        return;
      }
      this.formularioRegistro.controls.Email.setValue(this.emailValidado, {
        emitEvent: false,
      });
    });
  }

  private confirmarCambioTelefono(): void {
    this.confirmandoCambioTelefono = true;
    this.abrirConfirmacionCambio(
      "Cambiar teléfono",
      "Si cambias el teléfono tendrás que validarlo nuevamente. ¿Deseas continuar?",
    ).subscribe((confirmado) => {
      this.confirmandoCambioTelefono = false;
      if (confirmado) {
        this.reiniciarValidacionTelefono();
        return;
      }
      this.sincronizarTelefonoVisualDesdeCompleto(this.telefonoValidado);
      this.formularioRegistro.controls.Telefono.setValue(
        this.telefonoValidado,
        { emitEvent: false },
      );
    });
  }

  private abrirConfirmacionCambio(
    title: string,
    message: string,
  ): Observable<boolean> {
    return this.dialog
      .open(RegistroInformativoDialogComponent, {
        width: "440px",
        maxWidth: "calc(100vw - 32px)",
        autoFocus: false,
        data: {
          type: "warning",
          icon: "error_outline",
          title,
          message,
          showCancelButton: true,
          confirmText: "Continuar",
          cancelText: "Cancelar",
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef));
  }

  telefonoTieneFormatoCorrecto(): boolean {
    return (
      this.telefonoLocal.length === 10 &&
      !!this.codigoPaisTelefonoSeleccionado?.codigo &&
      this.formularioRegistro.controls.Telefono.valid
    );
  }

  telefonoPuedeVerificarse(): boolean {
    return (
      this.dosFaTelefonoHabilitado &&
      !this.consultandoNormaTelefono &&
      this.telefonoTieneFormatoCorrecto() &&
      !this.validandoTelefono &&
      !this.procesandoTelefono
    );
  }

  seleccionarCodigoPaisTelefono(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const codigo = this.codigosPaisTelefono.find(
      (item) => this.getCodigoPaisTelefonoKey(item) === select.value,
    );
    if (!codigo) {
      return;
    }
    this.codigoPaisTelefonoSeleccionado = codigo;
    this.sincronizarTelefonoCompleto(true);
  }

  capturarTelefonoLocal(event: Event): void {
    const input = event.target as HTMLInputElement;
    const valor = input.value.replace(/\D/g, "").slice(0, 10);
    input.value = valor;
    this.telefonoLocal = valor;
    this.sincronizarTelefonoCompleto(true);
  }

  private sincronizarTelefonoCompleto(marcar = false): void {
    const control = this.formularioRegistro.controls.Telefono;
    const telefono = this.telefonoLocal
      ? `${this.codigoPaisTelefonoSeleccionado.codigo}${this.telefonoLocal}`
      : "";
    control.setValue(telefono);

    if (marcar) {
      control.markAsDirty();
      control.markAsTouched();
    }

    control.updateValueAndValidity({ emitEvent: false });
    if (marcar && this.telefonoLocal && this.telefonoLocal.length !== 10) {
      control.setErrors({
        ...(control.errors || {}),
        telefonoLocalIncompleto: true,
      });
    }
  }

  private sincronizarTelefonoVisualDesdeCompleto(telefono: string): void {
    const valor = `${telefono || ""}`.replace(/\D/g, "");
    const codigo = [...this.codigosPaisTelefono]
      .sort((a, b) => b.codigo.length - a.codigo.length)
      .find((item) => valor.startsWith(item.codigo));
    this.codigoPaisTelefonoSeleccionado = codigo || this.codigosPaisTelefono[0];
    this.telefonoLocal = valor
      .slice(this.codigoPaisTelefonoSeleccionado.codigo.length)
      .slice(0, 10);
  }

  private cargarCatalogoPreguntas(): void {
    BLOCK_UI_REQUEST.Run(
      this.store,
      this.authService.catalogoPreguntas(),
      (respuesta: any) => {
        this.catPreguntas = Array.isArray(respuesta)
          ? (respuesta as CatalogoPregunta[])
          : [];
      },
    );
  }

  private cargarLadasTelefonicasInternacionales(): void {
    BLOCK_UI_REQUEST.Run(
      this.store,
      this.authService.obtenerLadasTelefonicasInternacionales(),
      (respuesta: any) => {
        const ladas = Array.isArray(respuesta?.Respuesta)
          ? respuesta.Respuesta
          : [];

        const codigosPais = ladas
          .filter(
            (item: LadaTelefonicaInternacionalResponse) =>
              item?.valor_dato !== null &&
              item?.valor_dato !== undefined &&
              !!item?.descripl_dato,
          )
          .map((item: LadaTelefonicaInternacionalResponse) => ({
            codigo: `${item.valor_dato}`.trim(),
            pais: `${item.descripl_dato}`.trim(),
            bandera: this.obtenerClavePais(`${item.descripl_dato}`),
          }));

        if (!codigosPais.length) {
          this.seleccionarMexicoComoLadaInicial();
          return;
        }

        this.codigosPaisTelefono = this.ordenarLadasTelefono(codigosPais);
        this.seleccionarMexicoComoLadaInicial();
        this.sincronizarTelefonoCompleto(false);
      },
    );
  }

  private ordenarLadasTelefono(
    ladas: CodigoPaisTelefono[],
  ): CodigoPaisTelefono[] {
    return [...ladas].sort((a, b) => {
      if (this.esMexicoLada(a)) return -1;
      if (this.esMexicoLada(b)) return 1;
      return this.normalizarTexto(a.pais).localeCompare(
        this.normalizarTexto(b.pais),
      );
    });
  }

  private esMexicoLada(lada: CodigoPaisTelefono): boolean {
    return lada.codigo === "52" || this.normalizarTexto(lada.pais) === "mexico";
  }

  private seleccionarMexicoComoLadaInicial(): void {
    const mexico = this.codigosPaisTelefono.find((item) =>
      this.esMexicoLada(item),
    );
    this.codigoPaisTelefonoSeleccionado = mexico || this.codigosPaisTelefono[0];
  }

  abrirInfoUsuario(): void {
    this.abrirDialogoInformativo(
      "error",
      "Usuario no válido",
      this.validacionUsuario || "El usuario no es válido.",
    );
  }

  abrirInfoAlias(): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: '720px',
      maxWidth: 'calc(100vw - 32px)',
      autoFocus: false,
      data: {
        type: 'info',
        icon: 'info',
        title: 'Alias o sobrenombre',
        message: 'El alias hace referencia a lo que verá al intentar iniciar sesión.',
        imageSrc: 'assets/img/registro/atlas-login-ejemplo.png',
        imageAlt: 'Ejemplo de alias al iniciar sesión',
        actionText: 'Entendido'
      }
    });
  }

  private obtenerErroresRegistro(): string[] {
    const errores = this.obtenerErroresFormulario();

    if (this.usuarioValido.pending) {
      errores.push("Usuario: La validación aún está en proceso.");
    } else if (!this.usuarioValido.is) {
      errores.push(
        `Usuario: ${this.validacionUsuario || "Debe ingresar un usuario válido."}`,
      );
    }

    if (this.validandoPassword) {
      errores.push("Contraseña: La validación aún está en proceso.");
    }
    if (
      this.formularioRegistro.controls.Password.value &&
      (this.generaErrorPasswordForm || !this.passwordValido)
    ) {
      errores.push("Contraseña: La contraseña tiene un formato incorrecto.");
    }
    if (this.consultandoNormaCorreo) {
      errores.push(
        "Email: La configuración de verificación aún está en proceso.",
      );
    } else if (this.dosFaCorreoHabilitado && !this.validandoCorreo) {
      errores.push("Email: El correo electrónico no está validado.");
    }

    if (this.consultandoNormaTelefono) {
      errores.push(
        "Teléfono celular: La configuración de verificación aún está en proceso.",
      );
    } else if (this.dosFaTelefonoHabilitado && !this.validandoTelefono) {
      errores.push("Teléfono celular: El teléfono no está validado.");
    }

    return [...new Set(errores)];
  }

  private obtenerErroresFormulario(): string[] {
    const errores: string[] = [];
    Object.keys(this.formularioRegistro.controls).forEach((controlName) => {
      if (["CodEmail", "CodTelefono", "ValidarUsuario"].includes(controlName)) {
        return;
      }
      const control = this.getControl(controlName);
      if (!control || control.disabled || !control.errors) {
        return;
      }
      errores.push(
        `${this.obtenerEtiquetaControl(controlName)}: ${this.obtenerMensajeErrorControl(controlName)}`,
      );
    });
    return errores;
  }

  private mostrarErroresRegistro(errores: string[]): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: "520px",
      maxWidth: "calc(100vw - 32px)",
      autoFocus: false,
      data: {
        type: "error",
        icon: "error_outline",
        title: "Revisa la información",
        message: "No fue posible continuar. Corrige lo siguiente:",
        messages: errores,
        actionText: "Entendido",
      },
    });
  }

  private abrirDialogoErroresBackend(error: any): void {
    const body = error?.error;
    const erroresBackend = Array.isArray(body)
      ? body
      : Array.isArray(body?.Errores)
        ? body.Errores
        : [];

    const mensajes = erroresBackend.length
      ? erroresBackend
          .map((item: any) => item?.Exception || item?.Message || item?.message)
          .filter(Boolean)
      : [
          body?.Message ||
            body?.Exception ||
            (typeof body === "string" ? body : "") ||
            `No fue posible registrar al ${this.configuracionRegistro().perfilNombre.toLowerCase()}.`,
        ];

    this.dialog.open(RegistroInformativoDialogComponent, {
      width: "520px",
      maxWidth: "calc(100vw - 32px)",
      autoFocus: false,
      data: {
        type: "error",
        icon: "error_outline",
        title: "Error al registrar",
        message: "No fue posible completar el registro.",
        messages: mensajes,
        actionText: "Entendido",
      },
    });
  }

  isInvalid(controlName: string): boolean {
    const control = this.getControl(controlName);
    return !!control && control.invalid && (control.touched || control.dirty);
  }

  getError(controlName: string): string {
    const control = this.getControl(controlName);
    if (!control || !control.errors || !(control.touched || control.dirty)) {
      return "";
    }
    return this.obtenerMensajeErrorControl(controlName);
  }

  abrirErrorCampo(controlName: string): void {
    this.abrirDialogoInformativo(
      "error",
      `${this.obtenerEtiquetaControl(controlName)} no válido`,
      this.obtenerMensajeErrorControl(controlName),
    );
  }

  private obtenerMensajeErrorControl(controlName: string): string {
    const control = this.getControl(controlName);
    if (!control?.errors) return "El campo no es válido.";
    if (control.hasError("required")) return "El campo es obligatorio.";
    if (control.hasError("match")) return "Las contraseñas no coinciden.";
    if (control.hasError("formatoIncorrecto"))
      return "La contraseña tiene un formato incorrecto.";
    if (control.hasError("telefonoLocalIncompleto"))
      return "Ingresa los 10 dígitos del teléfono.";
    if (control.hasError("email"))
      return "El correo no tiene un formato válido.";
    if (control.hasError("minlength") || control.hasError("minlengthCustom"))
      return "El valor es demasiado corto.";
    if (control.hasError("maxlength") || control.hasError("maxlengthCustom"))
      return "El valor excede la longitud permitida.";
    if (control.hasError("exists") || control.hasError("customError"))
      return this.validacionUsuario || "El usuario no es válido.";
    if (control.hasError("pattern"))
      return this.obtenerErrorPattern(controlName);
    return "El campo no es válido.";
  }

  private obtenerErrorPattern(controlName: string): string {
    const mensajes: Record<string, string> = {
      Usuario: "Solo se permiten letras, números y guion bajo.",
      Email: "El correo no tiene un formato válido.",
      Telefono: "Ingresa los 10 dígitos del teléfono.",
      RFC: "El RFC no tiene un formato válido.",
    };
    return mensajes[controlName] || "El formato no es válido.";
  }

  private obtenerEtiquetaControl(controlName: string): string {
    const etiquetas: Record<string, string> = {
      NumeroEmpleado: "Número de empleado",
      Usuario: "Usuario",
      Cuenta: "Clave de Ejecutivo",
      Password: "Contraseña",
      ConfirmaPassword: "Confirma contraseña",
      Alias: "Alias o sobrenombre",
      Email: "Email",
      Telefono: "Teléfono celular",
      IdPregunta: "Pregunta secreta",
      Respuesta: "Respuesta",
      Nombre: "Nombre",
      Paterno: "Apellido paterno",
      Materno: "Apellido materno",
      RFC: "RFC",
    };
    return etiquetas[controlName] || controlName;
  }

  private getControl(controlName: string): AbstractControl | null {
    return this.formularioRegistro.get(controlName);
  }

  private limpiarErroresControl(
    control: AbstractControl,
    errores: string[],
  ): void {
    if (!control.errors) return;
    const actuales = { ...control.errors };
    errores.forEach((error) => delete actuales[error]);
    control.setErrors(Object.keys(actuales).length ? actuales : null);
  }

  private mostrarErrorSolicitudCodigo(): void {
    this.abrirDialogoInformativo(
      "error",
      "No fue posible enviar el código",
      "Intenta solicitar tu código más tarde.",
    );
  }

  private abrirDialogoInformativo(
    type: "info" | "warning" | "error" | "success",
    title: string,
    message: string,
  ): void {
    this.dialog.open(RegistroInformativoDialogComponent, {
      width: "440px",
      maxWidth: "calc(100vw - 32px)",
      autoFocus: false,
      data: { type, title, message, actionText: "Aceptar" },
    });
  }

  desbloquearAutofill(event: FocusEvent): void {
    const input = event.target as HTMLInputElement;
    setTimeout(() => (input.readOnly = false), 80);
  }

  bloquearAutofill(event: FocusEvent): void {
    (event.target as HTMLInputElement).readOnly = true;
  }

  private obtenerClavePais(pais: string): string {
    const claves: Record<string, string> = {
      mexico: "MX",
      "estados unidos": "US",
      canada: "CA",
      brasil: "BR",
      argentina: "AR",
      colombia: "CO",
      chile: "CL",
      peru: "PE",
      espana: "ES",
      francia: "FR",
      alemania: "DE",
      italia: "IT",
      "reino unido": "GB",
      china: "CN",
      japon: "JP",
      india: "IN",
      australia: "AU",
    };
    return (
      claves[this.normalizarTexto(pais)] ||
      `${pais || ""}`.trim().slice(0, 2).toUpperCase()
    );
  }

  private normalizarTexto(valor: string): string {
    return `${valor || ""}`
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .trim();
  }

  get codigoPaisTelefonoSeleccionadoKey(): string {
    return this.getCodigoPaisTelefonoKey(this.codigoPaisTelefonoSeleccionado);
  }

  getCodigoPaisTelefonoKey(pais: CodigoPaisTelefono): string {
    return pais ? `${pais.codigo}|${this.normalizarTexto(pais.pais)}` : "";
  }
}
